import { useState } from 'react';
import {
  Box,
  Typography,
  Modal,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { investorApi, vendorApi } from '../../api/api';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
 
 
const PropertyGallery = ({ imageData: initialData, type }) => {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [imageData, setImageData] = useState(Array.isArray(initialData) ? initialData : []);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
 
 
  const handleOpen = (index) => {
    setSelectedIndex(index);
    setOpen(true);
  };
 
  const handleClose = () => setOpen(false);
 
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this image?"
    );
    if (!confirmDelete) return;
 
    try {
      const res = await vendorApi.deleteImageById({ type, id });
 
      // ✅ Remove image from UI
      setImageData((prev) =>
        prev.filter((img) => (img.id || img.Id) !== id)
      );
 
      // ✅ Show success message
      setSnackbar({
        open: true,
        message: res?.data?.message || "Image deleted successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete image:", error);
 
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.error ||
          "Failed to delete image",
        severity: "error",
      });
    }
  };
 
 
  return (
    <Box sx={{ mt: 4, px: { xs: 2, sm: 3, md: 5 }, width: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          color: "#13216e",
          fontWeight: 600,
          fontSize: { xs: "16px", sm: "18px", md: "20px" },
          mb: 2
        }}
      >
        Gallery Images
      </Typography>
 
      {Array.isArray(imageData) && imageData.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            pb: 2,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            whiteSpace: 'nowrap',
          }}
        >
          {imageData.map((image, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                flex: '0 0 auto',
                cursor: 'pointer',
              }}
            >
              <img
                src={image.ImageURL}
                alt={`Property ${index}`}
                style={{
                  height: '200px',
                  width: '100%',
                  maxWidth: '300px',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                }}
                onClick={() => handleOpen(index)}
              />
              <IconButton
                size="small"
                onClick={() => handleDelete(image.id ?? image.Id)}
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,0,0,0.6)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography>No images available</Typography>
      )}
 
      {/* Fullscreen Modal with Swiper Carousel */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
 
          <Swiper
            modules={[Navigation, Pagination, Keyboard]}
            initialSlide={selectedIndex}
            loop={true}
            navigation
            pagination={{ clickable: true }}
            keyboard={{ enabled: true }}
            direction="horizontal"
            style={{
              width: '100%',
              height: '100%',
              maxWidth: '100vw',
              maxHeight: '100vh',
            }}
          >
            {imageData.map((image, index) => (
              <SwiperSlide key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    px: 2,
                  }}
                >
                  <img
                    src={image.ImageURL}
                    alt={`Slide ${index}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '90vh',
                      objectFit: 'contain',
                      borderRadius: '10px',
                    }}
                  />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Modal>
 
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
 
    </Box>
  );
};
 
export default PropertyGallery;